import { Button, ComboBox, Form, NumberInput, TextInput } from '@carbon/react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { useTransactionPartiesAtom, ReconcilerDefaultsResponse } from './atoms';
import searchAtomPair from 'atoms/search-atom';
import { DocumentResponse } from 'types/api-responses';
import { debounce } from 'lodash-es';
import request from 'lib/request';
import { formatNumber } from 'utils/number';
import style from './reconciler.module.scss';

type ReconcilerFormProps = {
  onSubmit: (
    entityId: number,
    documentId: number,
    validationId?: string | null,
    overlapThreshold?: number,
    tolerance?: number,
    shapeMatchThreshold?: number
  ) => void;
  loading?: boolean;
  documentRef?: string | null;
  defaults: ReconcilerDefaultsResponse;
};

const formatDocumentDisplay = (doc: DocumentResponse): string => {
  const title = doc.documentTitle || 'Unknown';
  return `${title} (ID: ${doc.id})`;
};

export const ReconcilerForm = ({
  onSubmit,
  loading = false,
  documentRef,
  defaults,
}: ReconcilerFormProps) => {
  const location = useLocation();
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentResponse | null>(null);
  // Convert from decimal to percentage for display (e.g., 0.01 -> 1%)
  const [overlapThreshold, setOverlapThreshold] = useState(
    defaults.shapeInclusionThreshold * 100
  );
  const [shapeMatchThreshold, setShapeMatchThreshold] = useState(
    defaults.shapeSimilarityThreshold * 100
  );
  const [tolerance, setTolerance] = useState(
    defaults.interestDifferenceThreshold
  );
  const [toleranceInput, setToleranceInput] = useState(
    // Use formatNumber to avoid scientific notation for small numbers like 0.00000005
    formatNumber(defaults.interestDifferenceThreshold, 8, 8, 'decimal', false)
  );
  const hasAutoSubmitted = useRef(false);
  const firstRender = useRef(true);
  const userClearedDocument = useRef(false); // Track if user manually cleared the document

  useEffect(() => {
    if (userClearedDocument.current) {
      return;
    }

    if (documentRef) {
      // Parse documentRef format: "Unknown · 20191732"
      const parts = documentRef.split(' · ');
      if (parts.length === 2) {
        // Only update if instrumentNumber is still "Unknown" (hasn't been updated yet)
        setSelectedDocument((current) => {
          if (current && current.instrumentNumber === 'Unknown') {
            return {
              ...current,
              documentTitle: parts[0],
              instrumentNumber: parts[1],
            };
          }
          return current;
        });
      }
    }
  }, [documentRef]);

  // Document search setup
  const { searchAtom: documentSearchAtom, resetSearchDataAtom } = useMemo(
    () => searchAtomPair<DocumentResponse>('documents'),
    []
  );
  const [documentSearchResults, setDocumentSearchPayload] =
    useAtom(documentSearchAtom);
  const resetDocumentSearch = useSetAtom(resetSearchDataAtom);

  const debouncedDocumentSearch = useMemo(
    () => debounce(setDocumentSearchPayload, 400),
    [setDocumentSearchPayload]
  );

  useEffect(() => {
    firstRender.current = false;
  }, []);

  const documentResults = documentSearchResults?.data?.results || [];

  // Fetch transaction parties for selected document
  const documentId = selectedDocument?.id || null;
  const transactionPartiesAtom = useTransactionPartiesAtom(documentId);
  const [transactionPartiesState] = useAtom(transactionPartiesAtom);

  // Extract transaction parties data
  const transactionParties =
    transactionPartiesState.data && !('error' in transactionPartiesState.data)
      ? transactionPartiesState.data
      : [];
  const entitiesLoading = transactionPartiesState.loading;

  // Create entity items for ComboBox (same format as title validator)
  const entityItems = transactionParties.map((party) => {
    const capacity = party.role === 'grantee' ? 'Conveyee' : 'Conveyor';
    return {
      id: party.entity.id.toString(),
      label: `${party.entity.name} (${party.entity.id}) - ${capacity}`,
    };
  });

  const selectedEntityItem = entityItems.find(
    (item) => item.id === selectedEntityId
  );

  // Parse URL params and populate form immediately
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const documentIdParam = searchParams.get('document_id');
    const entityIdParam = searchParams.get('entity_id');
    const validationIdParam = searchParams.get('validation_id');

    // Set entity ID immediately
    if (entityIdParam && !selectedEntityId) {
      setSelectedEntityId(entityIdParam);
    }

    // If document_id is provided via URL, fetch and set up the document
    // BUT don't re-populate if user manually cleared it
    if (documentIdParam && !selectedDocument && !userClearedDocument.current) {
      const docId = parseInt(documentIdParam, 10);
      if (!isNaN(docId)) {
        // Create minimal document object immediately so reconciliation can run
        const minimalDoc = {
          id: docId,
          documentTitle: 'Unknown',
          instrumentNumber: 'Unknown',
        } as DocumentResponse;
        setSelectedDocument(minimalDoc);

        // Fetch full document details directly from API
        request
          .get<DocumentResponse>(`/documents/${docId}`)
          .then((response) => {
            if (
              'type' in response &&
              response.type === 'success' &&
              'data' in response
            ) {
              setSelectedDocument(response.data as DocumentResponse);
            }
          });
      }
    }

    // Auto-submit if all required params are present
    if (entityIdParam && documentIdParam && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      const entityIdNum = parseInt(entityIdParam, 10);
      const documentIdNum = parseInt(documentIdParam, 10);

      if (!isNaN(entityIdNum) && !isNaN(documentIdNum)) {
        onSubmit(
          entityIdNum,
          documentIdNum,
          validationIdParam,
          overlapThreshold / 100,
          tolerance,
          shapeMatchThreshold / 100
        );
      }
    }
  }, [
    location.search,
    selectedDocument,
    selectedEntityId,
    overlapThreshold,
    shapeMatchThreshold,
    tolerance,
    onSubmit,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEntityId) return;
    const entityIdNum = parseInt(selectedEntityId, 10);
    const documentIdNum = selectedDocument?.id;

    if (!isNaN(entityIdNum) && documentIdNum) {
      // Manual submit doesn't have validation_id, pass null
      // Convert percentage to decimal (e.g., 1% -> 0.01)
      onSubmit(
        entityIdNum,
        documentIdNum,
        null,
        overlapThreshold / 100,
        tolerance,
        shapeMatchThreshold / 100
      );
    }
  };

  const isValid = selectedEntityId !== null && selectedDocument !== null;

  const handleDocumentChange = ({ selectedItem }: any) => {
    // Handle clearing (X button clicked or manually cleared)
    if (!selectedItem) {
      userClearedDocument.current = true;
      setSelectedDocument(null);
      setSelectedEntityId(null);
      resetDocumentSearch(undefined);
      return;
    }
    // Handle document selection
    userClearedDocument.current = false;
    setSelectedDocument(selectedItem);
    // Reset entity when document changes
    if (selectedItem.id !== selectedDocument?.id) {
      setSelectedEntityId(null);
    }
  };

  const handleDocumentInputChange = (text: string) => {
    // Skip search on first render
    if (firstRender.current) return;
    // If text is empty, ensure document is cleared
    if (!text || text.trim() === '') {
      if (selectedDocument !== null) {
        setSelectedDocument(null);
        setSelectedEntityId(null);
      }
      resetDocumentSearch(undefined);
      return;
    }
    // Perform debounced search
    resetDocumentSearch(undefined);
    debouncedDocumentSearch((current) => ({
      ...current,
      text,
      pageSize: 10,
    }));
  };

  return (
    <Form onSubmit={handleSubmit} className={style.form}>
      <div className={style.formMainRow}>
        <ComboBox
          key={
            selectedDocument
              ? `doc-${selectedDocument.id}-${selectedDocument.documentTitle}`
              : 'no-doc'
          }
          id="document-search"
          titleText="Document"
          placeholder="Search by title, instrument number, or ID..."
          items={documentResults}
          itemToString={(doc) => (doc ? formatDocumentDisplay(doc) : '')}
          selectedItem={selectedDocument}
          onChange={handleDocumentChange}
          onInputChange={handleDocumentInputChange}
          disabled={loading}
        />
        <ComboBox
          id="target-entity-id"
          titleText="Target Entity"
          placeholder={
            !documentId
              ? 'Input a document ID first'
              : entitiesLoading
                ? 'Loading entities...'
                : entityItems.length === 0
                  ? 'No entities found on this document'
                  : 'Select an entity'
          }
          items={entityItems}
          itemToString={(item) => (item ? item.label : '')}
          selectedItem={selectedEntityItem || null}
          shouldFilterItem={({ item, inputValue }) => {
            if (!inputValue) return true;
            if (selectedEntityItem && inputValue === selectedEntityItem.label)
              return true;
            const searchValue = inputValue.toLowerCase();
            return item.label.toLowerCase().includes(searchValue);
          }}
          onChange={({ selectedItem }) => {
            if (selectedItem) {
              setSelectedEntityId(selectedItem.id);
            } else {
              setSelectedEntityId(null);
            }
          }}
          disabled={!documentId || entitiesLoading || loading}
        />
      </div>
      <div className={style.formOptionsRow}>
        <div className={style.formOptionsLeft}>
          <NumberInput
            id="overlap-threshold"
            label="Shape Inclusion Threshold (%)"
            value={Number(overlapThreshold.toFixed(2))}
            min={0.01}
            max={100}
            step={0.01}
            onChange={(
              _e: unknown,
              { value }: { value: number | string | undefined }
            ) => {
              const numValue =
                typeof value === 'string' ? parseFloat(value) : value;
              if (
                numValue !== undefined &&
                numValue !== null &&
                !isNaN(numValue) &&
                numValue >= 0.01 &&
                numValue <= 100
              ) {
                // Round to 2 decimal places
                const rounded = Math.round(numValue * 100) / 100;
                setOverlapThreshold(rounded);
              }
            }}
            invalidText="Threshold must be between 0.01% and 100%"
            disabled={loading}
          />
          <NumberInput
            id="shape-match-threshold"
            label="Shape Similarity Threshold (%)"
            value={Number(shapeMatchThreshold.toFixed(0))}
            min={1}
            max={100}
            step={1}
            onChange={(
              _e: unknown,
              { value }: { value: number | string | undefined }
            ) => {
              const numValue =
                typeof value === 'string' ? parseFloat(value) : value;
              if (
                numValue !== undefined &&
                numValue !== null &&
                !isNaN(numValue) &&
                numValue >= 1 &&
                numValue <= 100
              ) {
                setShapeMatchThreshold(Math.round(numValue));
              }
            }}
            invalidText="Threshold must be between 1% and 100%"
            disabled={loading}
          />
          <TextInput
            id="tolerance-threshold"
            labelText="Interest Difference Threshold"
            value={toleranceInput}
            onChange={(e) => {
              const inputValue = e.target.value;
              setToleranceInput(inputValue);

              // Try to parse as a number
              const numValue = parseFloat(inputValue);
              if (
                !isNaN(numValue) &&
                numValue >= 0.00000001 &&
                numValue <= 0.01
              ) {
                setTolerance(numValue);
              }
            }}
            invalid={(() => {
              const numValue = parseFloat(toleranceInput);
              return (
                isNaN(numValue) || numValue < 0.00000001 || numValue > 0.01
              );
            })()}
            invalidText="Tolerance must be a number between 0.00000001 and 0.01"
            disabled={loading}
            placeholder={formatNumber(
              defaults.interestDifferenceThreshold,
              8,
              8,
              'decimal',
              false
            )}
          />
        </div>
        <Button type="submit" disabled={!isValid || loading}>
          Reconcile
        </Button>
      </div>
      <p className={style.formHelperText}>
        See &quot;How it works&quot; on the top right for configuration details
      </p>
    </Form>
  );
};
